import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { BUILD_STAGES, type BuildActivity } from '@ideoz/core';
import { cn } from '@ideoz/ui';

export interface BuildProgressCardProps {
  activity: BuildActivity;
  /** When the build started, for the total elapsed time. */
  buildStartedAt: string | null;
  className?: string;
  testId?: string;
}

function useSecondsSince(iso: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, Math.round((now - t) / 1000));
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/**
 * What a running build is doing, under the prototyper loading animation: the current action in
 * the founder's words (it fades to the next one as the build moves on), the time so far, and a
 * slim four-part bar for Plan, Change, Check, Ready. The chat timeline shows the same words
 * (`describeBuildActivity`).
 */
export function BuildProgressCard({
  activity,
  buildStartedAt,
  className,
  testId = 'build-progress-card',
}: BuildProgressCardProps): JSX.Element {
  const elapsed = useSecondsSince(buildStartedAt);
  const current = BUILD_STAGES.findIndex((s) => s.stage === activity.stage);

  return (
    <div
      className={cn('flex w-full max-w-xs flex-col items-center gap-3 text-center', className)}
      data-testid={testId}
      role="status"
      aria-live="polite"
    >
      <div className="flex min-h-[20px] items-center justify-center gap-2">
        <AnimatePresence mode="wait" initial={false}>
          <motion.p
            key={activity.action}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="text-sm font-medium text-primary-800"
          >
            {activity.action}…
          </motion.p>
        </AnimatePresence>
        {elapsed != null ? (
          <span className="text-xs tabular-nums text-slate-400">{formatElapsed(elapsed)}</span>
        ) : null}
      </div>

      <div className="w-full" aria-label={`Step ${current + 1} of ${BUILD_STAGES.length}`}>
        <div className="flex gap-1.5">
          {BUILD_STAGES.map((s, i) => (
            <span
              key={s.stage}
              className={cn(
                'h-1 flex-1 rounded-full transition-colors duration-300',
                i < current && 'bg-primary-500',
                i === current && 'bg-primary-300 animate-pulse',
                i > current && 'bg-slate-200'
              )}
            />
          ))}
        </div>
        <div className="mt-1.5 flex">
          {BUILD_STAGES.map((s, i) => (
            <span
              key={s.stage}
              className={cn(
                'flex-1 text-[11px]',
                i === current ? 'font-semibold text-primary-700' : 'text-slate-400'
              )}
              aria-current={i === current ? 'step' : undefined}
            >
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {activity.fixing ? (
        <p className="text-xs text-slate-500">
          The check found a problem; it is being fixed before the new version opens.
        </p>
      ) : null}
    </div>
  );
}

export default BuildProgressCard;
